import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { X, User, Phone, AlertCircle, Users, Globe } from 'lucide-react';
import { contactService } from '../../services/contactService';
import toast from 'react-hot-toast';

const ContactModal = ({ customerId, contact, onClose, onSaved }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [contactGroups, setContactGroups] = useState([]);
  const isEditMode = !!contact;
  
  const { register, handleSubmit, formState: { errors }, setValue, reset } = useForm();

  useEffect(() => {
    if (contact) {
      // Populate form for editing
      setValue('contactName', contact.contactName || '');
      setValue('phoneNumber', contact.phoneNumber || '');
      setValue('countryCode', contact.countryCode || '');
      setValue('isSubscribed', contact.isSubscribed !== undefined ? contact.isSubscribed : true);
    } else {
      reset();
    }
  }, [contact, setValue, reset]);

  // Load contact groups when component mounts
  useEffect(() => {
    if (customerId) {
      loadContactGroups();
    }
  }, [customerId]);

  const loadContactGroups = async () => {
    try {
      const groups = await contactService.getContactGroups(customerId);
      setContactGroups(groups || []);
    } catch (error) {
      console.error('Error loading contact groups:', error);
      // Don't show error toast as this is not critical for the form
    }
  };

  const onSubmit = async (data) => {
    if (!customerId) {
      toast.error('Customer ID is required');
      return;
    }

    setIsLoading(true);
    try {
      if (isEditMode) {
        // Update existing contact
        const updateData = {
          contactName: data.contactName,
          phoneNumber: data.phoneNumber,
          countryCode: data.countryCode,
          isSubscribed: data.isSubscribed
        };
        await contactService.updateContact(contact.contactId, updateData);
        toast.success('Contact updated successfully');
      } else {
        // Create new contact
        const contactData = {
          ...data,
          contactGroupName: data.contactGroup || null // Map contactGroup to contactGroupName
        };
        await contactService.createContact(contactData);
        toast.success('Contact created successfully');
      }
      
      onSaved();
    } catch (error) {
      console.error('Error saving contact:', error);
      const errorMessage = error.response?.data?.message || error.message || `Failed to ${isEditMode ? 'update' : 'save'} contact`;
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {isEditMode ? 'Edit Contact' : 'Add New Contact'}
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {/* Contact Name */}
              <div>
                <label htmlFor="contactName" className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="contactName"
                    type="text"
                    className={`input-field pl-10 ${errors.contactName ? 'input-error' : ''}`}
                    placeholder="Enter contact name (optional)"
                    {...register('contactName', {
                      minLength: { value: 2, message: 'Name must be at least 2 characters' },
                      maxLength: { value: 50, message: 'Name must not exceed 50 characters' }
                    })}
                  />
                </div>
                {errors.contactName && (
                  <p className="mt-1 text-sm text-error-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.contactName.message}
                  </p>
                )}
              </div>

              {/* Country Code */}
              <div>
                <label htmlFor="countryCode" className="block text-sm font-medium text-gray-700 mb-1">
                  Country Code <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Globe className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="countryCode"
                    type="text"
                    className={`input-field pl-10 ${errors.countryCode ? 'input-error' : ''}`}
                    placeholder="Enter country code (e.g., +91)"
                    {...register('countryCode', {
                      required: 'Country code is required',
                      pattern: {
                        value: /^\+[1-9]\d{1,3}$/,
                        message: 'Country code must start with + followed by 1-4 digits'
                      }
                    })}
                  />
                </div>
                {errors.countryCode && (
                  <p className="mt-1 text-sm text-error-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.countryCode.message}
                  </p>
                )}
              </div>

              {/* Phone Number */}
              <div>
                <label htmlFor="phoneNumber" className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Phone className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="phoneNumber"
                    type="tel"
                    className={`input-field pl-10 ${errors.phoneNumber ? 'input-error' : ''}`}
                    placeholder="Enter phone number"
                    disabled={isEditMode}
                    {...register('phoneNumber', {
                      required: 'Phone number is required',
                      pattern: {
                        value: /^[6-9]\d{9}$/,
                        message: 'Phone number must be a valid 10-digit number starting with 6, 7, 8, or 9'
                      }
                    })}
                  />
                </div>
                {errors.phoneNumber && (
                  <p className="mt-1 text-sm text-error-600 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    {errors.phoneNumber.message}
                  </p>
                )}
              </div>



              {/* Subscription Status */}
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    {...register('isSubscribed')}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    defaultChecked={contact?.isSubscribed !== undefined ? contact.isSubscribed : true}
                  />
                  <span className="text-sm font-medium text-gray-700">Subscribed</span>
                </label>
              </div>

              {/* Contact Groups - Only show when creating */}
              {!isEditMode && (
                <div className="relative z-[10000]">
                  <label htmlFor="contactGroup" className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Group
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Users className="h-5 w-5 text-gray-400" />
                    </div>
                    <select
                      id="contactGroup"
                      className={`input-field pl-10 ${errors.contactGroup ? 'input-error' : ''}`}
                      {...register('contactGroup')}
                      style={{ zIndex: 10001 }}
                    >
                      <option value="">Select a group (optional)</option>
                      {contactGroups.map((group) => (
                        <option key={group.groupId} value={group.groupName}>
                          {group.groupName} ({group.totalContacts} contacts)
                        </option>
                      ))}
                    </select>
                  </div>
                  {errors.contactGroup && (
                    <p className="mt-1 text-sm text-error-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {errors.contactGroup.message}
                    </p>
                  )}
                </div>
              )}


              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="btn-secondary"
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="btn-primary flex items-center"
                >
                  {isLoading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  ) : null}
                  {isEditMode ? 'Update Contact' : 'Add Contact'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactModal;
